import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { DynamicGuardService } from '@workflow/dynamic-guard/dynamic-guard.decorator';
import { RouterUrlConstant, LoginConstant } from '../constant/information.const';
import { StatefulService } from '@app/service/stateful.service';

const applicationStage = `api:loan/application/loanApplications/$/applicationStage`;
const loginType = `api:loginType`;

/**
 * Route guard for application pages to check if the application is not in Post App stage
 */
@DynamicGuardService('ApplicationStageGuard')
@Injectable({ providedIn: 'root' })
export class ApplicationStageGuard {
  constructor(private readonly router: Router) {}
  public statefulService = inject(StatefulService);
  /**
   * Activate page if the page is enabled, other wise it will redirect to the first incomplete page in the current flow
   * If user has registered as a new user and completely filled out
   * the application, then submitted the application for the first time
   * and was redirected to the post app, or if the user logged out then
   * logged back in and was redirected to the post app, and then the user
   * decided to try to change the previously filled application.  Then redirect
   * the user back to the post app, by checking if there is a loan Id.
   */
  public canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        this.statefulService.wrap(applicationStage, 0).load(() => {
          this.checkApplicationStage(resolve, state);
        });
      } catch (e) {
        /* The initial access to the pricing page doesn't have any data about application stage
         * If this is the case then, return true to the promise and render the page.
         * If the try catch is not here, the initial call to the pricing page will render blank.
         */
        resolve(true);
      }
    });
  }

  private checkApplicationStage(resolve, state: RouterStateSnapshot): void {
    setTimeout(() => {
      if (!this.isOAOFlow && this.isPostAppStage && !state.url.includes('pricing-questions')) {
        this.router.navigate([RouterUrlConstant.Dashboard]);
        resolve(false);
      } else {
        resolve(true);
      }
    }, 0);
  }

  /**
   * Check Post Application Logic
   */
  private get isPostAppStage(): boolean {
    const stage = this.statefulService.wrap(applicationStage, 0).valueString;
    return stage && stage.toLowerCase() === LoginConstant.postApplication.toString().toLowerCase();
  }

  /**
   * Check OAO flow Logic
   */
  private get isOAOFlow(): boolean {
    const objLoginType = this.statefulService.wrap(loginType).valueString;
    return objLoginType && objLoginType.toLowerCase() === 'oao';
  }
}
